import React, { useContext, useEffect, useState, useMemo, useRef } from 'react';
import AuthContext from './auth';
import jwt_decode from 'jwt-decode';
import Modal from '../components/Utility/Modal/Modal';
import { getConfig } from '../helpers/config';
import { FormUtility } from '../components/Utility/FormUtility';

const StatusContext = React.createContext({
	addEvent: async (eventName) => {},
	removeEvent: async (eventName) => {},
	checkEvent: async (eventName) => {},
	awaitingLogout: async (value) => {}
});

export const StatusContextProvider = (props) => {

	const authContext = useContext(AuthContext);

	const thresholdForcedLogout = 3000;

	const limitExpirationRefreshToken = 60 * 1000;

	const awaitingModalChoice = useRef(false);

	const [awaitingToLogout, setAwaitingToLogout] = useState(false);

	const [modalShow, setModalShow] = useState(false);
	const [title, setTitle] = useState('Sessione scaduta');
	const [content, setContent] = useState('Rieseguire l\'accesso');
	const [actionLabel, setActionLabel] = useState('Ok');

	const environment = getConfig();

	let timeoutCheckToken = null;

	const utility = new FormUtility();

	useEffect(() => {
		if (!authContext.isLoggedIn) {
			removeAndClearAllEvents();
			return;
		}

		if (awaitingModalChoice.current) {
			return;
		}

		if (environment?.profile === 'dev') {
			return;
		}

		startCheckTokenTimeout();
	}, [authContext.isLoggedIn]);

	useEffect(() => {
		if (authContext.userInfo?.extras.fiscalNumber) {
			const fiscalCode = authContext.userInfo?.extras.fiscalNumber.replace('TINIT-', '');
			const eta = utility.ottieniEtaDaCodiceFiscale(fiscalCode);
			if (eta < 18) {
				awaitingModalChoice.current = true;
				removeAndClearAllEvents();
				setModalShow(true);
				setTitle('Attenzione!');
				setContent('L’accesso all’applicativo “Libri Gratis” non è consentito ai minori di 18 anni');
				setActionLabel('Rieseguire l\'accesso');
				return;
			}
		}
	}, [authContext.userInfo]);

	const removeAndClearAllEvents = () => {
		removeEventHandler('beforeunload');
		removeEventHandler('onbeforeunload');
		window.onbeforeunload = null;
		clearTimeout(timeoutCheckToken);
	}

	const checkTokenExpiring = async (token) => {
		if (token === null || token === undefined) {
			return;
		}

		if (token === 'undefined') {
			authContext.forceLogout(true);
		}

		const tokenD = jwt_decode(token);

		const expiration = (tokenD.exp * 1000) - new Date().getTime();
		if (expiration > limitExpirationRefreshToken) {
			return;
		}

		if (expiration <= 1000) {
			awaitingModalChoice.current = true;
			removeAndClearAllEvents();
			setModalShow(true);
			setTitle('Sessione scaduta');
			setContent('Rieseguire l\'accesso');
			setActionLabel('Ok');
			return;
		}

		clearTimeout(timeoutCheckToken);

		authContext.refresh()
							 .then((response) => response.json())
							 .then(async json => {
									const token = json.access_token;
									const refreshToken = json.refresh_token;
									authContext.refreshOnlyTokens(token, refreshToken);
									startCheckTokenTimeout();
							 }).catch(() => {
									setModalShow(true);
									setTitle('Sessione scaduta');
									setContent('Rieseguire l\'accesso');
									setActionLabel('Ok');
									return;
							 });
	}

	const startCheckTokenTimeout = () => {
		timeoutCheckToken = setInterval(() => {
			if (awaitingModalChoice.current) {
				return;
			}

			const token = sessionStorage.getItem('access_token');
			checkTokenExpiring(token);
		}, getTimeoutCheckingTime());
	}

	const getTimeoutCheckingTime = async () => {
		const token = sessionStorage.getItem('access_token');
		const tokenD = jwt_decode(token);
		const remainingTime = (tokenD.exp * 1000) - new Date().getTime();
		if (remainingTime < thresholdForcedLogout) {
			awaitingModalChoice.current = true;
			removeAndClearAllEvents();
			setModalShow(true);
			setTitle('Sessione scaduta');
			setContent('Rieseguire l\'accesso');
			setActionLabel('Ok');
			return;
		}

		return remainingTime < 15000 ? 2000 : 15000;
	}

	const addEventHandler = (eventName) => {
		window[eventName] = alertUser;
	}

	const removeEventHandler = (eventName) => {
		window[eventName] = null;
	}

	const checkEventHandler = (eventName) => {
		return !!window[eventName];
	}

	const alertUser = (e) => {
		e.preventDefault();
		e.returnValue = '';
	};

	const awaitingLogoutHandler = (value) => {
		setAwaitingToLogout(value);
	}

	const handleModalAction = async () => {
		const token = sessionStorage.getItem('access_token');
		removeAndClearAllEvents();
		authContext.clearSessionStorage(false);
		awaitingModalChoice.current = false;
		await authContext.logout(token, true, true);
		setModalShow(false);
	}

	const contextValue = useMemo(() => ({
		addEvent: addEventHandler,
		removeEvent: removeEventHandler,
		checkEvent: checkEventHandler,
		awaitingLogout: awaitingLogoutHandler,
		alertTimeout: modalShow,
		awaitingToLogout: awaitingToLogout
	}), [modalShow, awaitingToLogout]);

	return (
		<>
			<StatusContext.Provider value={contextValue}>
				{ props.children }
			</StatusContext.Provider>

			<Modal
        isOpen={modalShow}
        title={title}
        content={content}
        onClose={() => setModalShow(false)}
        onAction={handleModalAction}
        actionLabel={actionLabel}
				hideCloseButton
      />
		</>
	);
};

export default StatusContext;
